import React, { useMemo } from 'react';
import { 
  XAxis, 
  YAxis, 
  ResponsiveContainer,
  CartesianGrid,
  Area,
  AreaChart,
  Line
} from 'recharts';
import { ProjectUnlockData } from '@/types/project';
import { useTheme } from '@/hooks/useTheme';

interface UnlockTimelineChartProps {
  unlockData: ProjectUnlockData;
  variant?: 'desktop' | 'mobile';
}

interface TimelineDataPoint {
  date: string;
  label: string;
  unlocked: number;
  locked: number;
}

const UnlockTimelineChart: React.FC<UnlockTimelineChartProps> = ({ unlockData, variant = 'desktop' }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isMobile = variant === 'mobile';

  const timelineData = useMemo(() => {
    const startDate = new Date(unlockData.vesting_start_date);
    const endDate = new Date(unlockData.vesting_end_date);

    const startMs = startDate.getTime();
    const endMs = endDate.getTime();
    const duration = Math.max(endMs - startMs, 1);
    const points = 7;
    const step = points - 1;

    return Array.from({ length: points }, (_, index): TimelineDataPoint => {
      const ratio = index / step;
      const pointDate = new Date(startMs + duration * ratio);
      const currentUnlocked = unlockData.total_unlocked + (unlockData.total_locked * ratio);

      return {
        date: pointDate.toISOString().split('T')[0],
        label: pointDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        unlocked: Math.min(currentUnlocked, unlockData.total_supply),
        locked: unlockData.total_supply
      };
    });
  }, [unlockData]);

  const formatYAxisValue = (value: number) => {
    if (value === 0) return '0';
    if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    return `${value}`;
  };

  if (timelineData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="font-['Plus_Jakarta_Sans'] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          No timeline data available
        </p>
      </div>
    );
  }

  const yTicks = [
    0,
    unlockData.total_supply * 0.25,
    unlockData.total_supply * 0.5,
    unlockData.total_supply * 0.75,
    unlockData.total_supply,
  ];

  return (
    <div className="h-full w-full flex flex-col">
      <div className={isMobile ? 'h-[244px] w-full' : 'h-[242px] w-full'}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={timelineData}
            margin={isMobile ? { top: 2, right: 0, left: -10, bottom: 0 } : { top: 2, right: 0, left: -4, bottom: 0 }}
          >
            <CartesianGrid
              vertical={false}
              stroke={isDark ? 'rgba(253,253,253,0.12)' : '#e0e0e0'}
              strokeDasharray="4 4"
            />
            <XAxis
              dataKey="date"
              ticks={timelineData.map((point) => point.date)}
              tickFormatter={(value) => {
                const point = timelineData.find((entry) => entry.date === value);
                return point?.label || value;
              }}
              axisLine={false}
              tickLine={false}
              tickMargin={isMobile ? 10 : 16}
              interval={0}
              tick={{
                fontSize: isMobile ? 10 : 11,
                fill: isDark ? 'rgba(253,253,253,0.64)' : '#878787',
                fontFamily: 'Plus Jakarta Sans',
                fontWeight: 400,
              }}
            />
            <YAxis
              ticks={yTicks}
              tickFormatter={formatYAxisValue}
              axisLine={false}
              tickLine={false}
              width={isMobile ? 38 : 52}
              tick={{
                fontSize: 13,
                fill: isDark ? 'rgba(253,253,253,0.64)' : '#878787',
                fontFamily: 'Plus Jakarta Sans',
                fontWeight: 500,
              }}
            />

            <Area
              type="linear"
              dataKey="locked"
              stroke="none"
              fill="#ee9d1a"
              fillOpacity={0.35}
              isAnimationActive={false}
            />
            <Area
              type="linear"
              dataKey="unlocked"
              stroke="none"
              fill="#a9c804"
              fillOpacity={0.32}
              isAnimationActive={false}
            />
            <Line
              type="linear"
              dataKey="unlocked"
              stroke="#a9c804"
              strokeWidth={isMobile ? 1.5 : 2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="linear"
              dataKey="locked"
              stroke="#ee9d1a"
              strokeWidth={isMobile ? 1.5 : 2}
              dot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className={isMobile ? 'mt-4 flex items-center justify-center gap-4' : 'mt-6 flex items-center justify-center gap-8'}>
        <div className={isMobile ? 'flex items-center gap-2' : 'flex items-center gap-3'}>
          <span className="block h-[8px] w-[19px] bg-[#a9c804]" />
          <span className={`font-['Plus_Jakarta_Sans'] font-medium ${isMobile ? 'text-[12px] leading-[16px]' : 'text-[14px] leading-[20px]'} text-[#2d2923]`}>
            Unlocked Tokens
          </span>
        </div>
        <div className={isMobile ? 'flex items-center gap-2' : 'flex items-center gap-3'}>
          <span className="block h-[8px] w-[19px] bg-[#ee9d1a]" />
          <span className={`font-['Plus_Jakarta_Sans'] font-medium ${isMobile ? 'text-[12px] leading-[16px]' : 'text-[14px] leading-[20px]'} text-[#2d2923]`}>
            Locked Tokens
          </span>
        </div>
      </div>
    </div>
  );
};

export default UnlockTimelineChart;
